import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { LockKey, ClipboardText, Bell, Prohibit, DownloadSimple, ShieldCheck } from "@phosphor-icons/react";
import SiteNav from "../components/site/SiteNav";
import SiteFooter from "../components/site/SiteFooter";
import { ABOUT_TABS } from "../lib/site";
import { API } from "../lib/api";

function useSite(kind) {
  const [items, setItems] = useState(null);
  useEffect(() => { axios.get(`${API}/site/${kind}`).then((r) => setItems(r.data)).catch(() => setItems([])); }, [kind]);
  return items;
}
const monDay = (d) => { if (!d) return ["[MON]", "[00]"]; const x = new Date(d); return [x.toLocaleString("en", { month: "short" }).toUpperCase(), String(x.getDate()).padStart(2, "0")]; };
const img = (i) => `${API}/site/item/${i.id}/image`;
const ext = (u) => (u ? { href: u, target: "_blank", rel: "noreferrer" } : { href: "#" });

const Row = ({ k, mon = "[MON]", day = "[00]", title, sub, action = "Register", url }) => (
  <div className="flex items-center gap-4 rounded-2xl border border-slate-200 p-4 lift bg-white" data-testid={`${k}-row`}>
    <div className="w-14 h-14 shrink-0 rounded-xl bg-[#FCE4E7] text-[#D7263D] flex flex-col items-center justify-center leading-none">
      <span className="text-[10px] font-bold">{mon}</span><span className="text-lg font-extrabold mt-0.5">{day}</span></div>
    <div className="flex-1 min-w-0"><div className="font-bold">{title}</div><div className="text-xs text-slate-500 mt-0.5">{sub}</div></div>
    <a {...ext(url)} className="text-sm font-bold text-[#D7263D] underline shrink-0">{action}</a>
  </div>
);

const Events = () => {
  const items = useSite("event");
  if (items?.length) return <div className="space-y-3">{items.map((e, i) => { const [m, d] = monDay(e.date); return <Row key={e.id} k={`event-${i}`} mon={m} day={d} title={e.title} sub={[e.summary, e.location, e.category].filter(Boolean).join(" · ")} url={e.url} />; })}</div>;
  return (<div className="space-y-3">
  {[["Community launch"], ["School safety day"], ["Responder training"]].map(([t], i) => <Row key={t} k={`event-${i}`} title="[Event name]" sub={`${t} · [Venue, town] · [Time]`} />)}
</div>);
};

const Partners = () => {
  const items = useSite("partner");
  return (<div>
  <p className="text-sm text-slate-600 mb-5">Organisations working with bsafe to protect people across East Africa.</p>
  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">{items?.length ? items.map((p) => (
    <a key={p.id} {...ext(p.url)} title={p.summary} data-testid={`partner-${p.id}`} className="h-24 rounded-2xl bg-[#F2F4F8] flex items-center justify-center p-3 lift">
      {p.has_image ? <img src={img(p)} alt={p.title} className="max-h-full max-w-full object-contain" /> : <span className="font-bold text-sm text-center">{p.title}</span>}</a>))
    : [1, 2, 3, 4, 5].map((i) => <div key={i} className="h-24 rounded-2xl bg-[#F2F4F8] flex items-center justify-center text-xs text-slate-500">[Partner logo]</div>)}</div>
  <a href="#" className="mt-6 inline-block text-sm font-bold text-[#D7263D] underline">Become a partner →</a>
</div>);
};

const Teams = () => (<div className="grid md:grid-cols-3 gap-4">
  <div className="rounded-3xl bg-[#F2F4F8] p-6 text-center" data-testid="team-founder">
    <img src="/landing/founder.webp" alt="Ibrahim Mugawe" className="w-24 h-24 mx-auto rounded-full object-cover border-4 border-white" />
    <div className="mt-3 font-bold">Ibrahim Mugawe</div><div className="text-xs text-slate-500">Founder & CEO</div></div>
  {[1, 2].map((i) => <div key={i} className="rounded-3xl bg-[#F2F4F8] p-6 text-center">
    <div className="w-24 h-24 mx-auto rounded-full bg-white flex items-center justify-center text-[10px] text-slate-400">[Photo]</div>
    <div className="mt-3 font-bold">[Team member]</div><div className="text-xs text-slate-500">[Role]</div></div>)}
</div>);

const Training = () => {
  const items = useSite("training");
  if (items?.length) return <div className="space-y-3">{items.map((e, i) => { const [m, d] = monDay(e.date); return <Row key={e.id} k={`training-${i}`} mon={m} day={d} title={e.title} sub={[e.location, e.summary, e.category].filter(Boolean).join(" · ")} action="Book" url={e.url} />; })}</div>;
  return (<div className="space-y-3">
  {["First responder basics", "Using bsafe at your school", "Reader station for attendance"].map((t, i) => <Row key={t} k={`training-${i}`} title={t} sub="[Venue or online] · [Duration]" action="Book" />)}
</div>);
};

const Security = () => {
  const pts = [[LockKey, "Encrypted medical data", "Medical details are encrypted at rest. Group admins only see how complete a profile is, never the details."],
    [ShieldCheck, "You choose what responders see", "Each field is marked 'show on responder page' or 'private'. Minors only show a first name."],
    [ClipboardText, "Every access is logged", "Every scan and every nurse or admin view is recorded in an audit log you can read."],
    [Bell, "Alerts when your tag is opened", "Your family gets a WhatsApp message when the profile is opened. Link-preview bots are ignored."],
    [Prohibit, "Switch off a lost tag", "A lost or deactivated tag shows a neutral 'tag inactive' page straight away, and its link can be rotated."],
    [DownloadSimple, "Export or delete anytime", "Download your data or delete your profile, in line with Kenya and Uganda data protection laws."]];
  return <div className="grid md:grid-cols-2 gap-4">{pts.map(([I, t, d]) => (
    <div key={t} className="rounded-2xl border border-slate-200 p-5 flex gap-3"><span className="w-9 h-9 shrink-0 rounded-xl bg-[#FCE4E7] text-[#D7263D] flex items-center justify-center"><I size={18} weight="bold" /></span>
      <div><div className="font-bold text-sm">{t}</div><div className="text-xs text-slate-600 mt-1">{d}</div></div></div>))}</div>;
};

const Blog = () => {
  const items = useSite("blog");
  if (items?.length) return <div className="grid md:grid-cols-3 gap-4">{items.map((b) => (
    <a key={b.id} {...ext(b.url)} className="rounded-3xl border border-slate-200 overflow-hidden lift block" data-testid={`blog-${b.id}`}>
      {b.has_image ? <img src={img(b)} alt={b.title} className="h-36 w-full object-cover" /> : <div className="h-36 bg-[#E3E9F4]" />}
      <div className="p-4"><div className="text-[10px] font-extrabold tracking-[0.15em] text-[#D7263D]">{(b.category || "News").toUpperCase()}</div><div className="font-bold mt-1">{b.title}</div>
        {b.summary && <div className="text-xs text-slate-600 mt-1 line-clamp-2">{b.summary}</div>}
        <div className="text-xs text-slate-500 mt-1">{[b.date && new Date(b.date).toLocaleDateString(), b.location].filter(Boolean).join(" · ")}</div></div>
    </a>))}</div>;
  return (<div className="grid md:grid-cols-3 gap-4">{[1, 2, 3].map((i) => (
  <a key={i} href="#" className="rounded-3xl border border-slate-200 overflow-hidden lift block" data-testid={`blog-${i}`}>
    <div className="h-36 bg-[#E3E9F4] flex items-center justify-center text-xs text-slate-500">[Article image]</div>
    <div className="p-4"><div className="text-[10px] font-extrabold tracking-[0.15em] text-[#D7263D]">[CATEGORY]</div><div className="font-bold mt-1">[Article title]</div><div className="text-xs text-slate-500 mt-1">[Date] · [Read time]</div></div>
  </a>))}</div>);
};

const PANELS = { events: Events, partners: Partners, teams: Teams, training: Training, security: Security, blog: Blog };

export default function About() {
  const [params, setParams] = useSearchParams();
  const tab = PANELS[params.get("tab")] ? params.get("tab") : "events";
  const Panel = PANELS[tab];
  return (
    <div className="landing bg-white min-h-screen text-[#16294A]" style={{ fontFamily: "Sora, sans-serif" }} data-testid="about-page">
      <SiteNav active="about" />
      <section className="bg-[#16294A] text-white">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 py-16">
          <div className="text-xs font-bold tracking-[0.25em] text-[#FF7A8A]">ABOUT bsafe</div>
          <h1 className="mt-4 text-4xl sm:text-5xl font-extrabold max-w-2xl leading-[1.05]">Built in Kampala so that help always knows who you are</h1>
          <p className="mt-5 text-sm text-slate-300 max-w-md">bsafe is an emergency medical ID for East Africa, made by Mugawe Investments U Ltd.</p>
        </div>
      </section>
      <main className="max-w-[1200px] mx-auto px-5 sm:px-8 py-10">
        <div className="flex flex-wrap gap-2" data-testid="about-tabs">{ABOUT_TABS.map(([k, l]) => (
          <button key={k} onClick={() => setParams({ tab: k })} data-testid={`about-tab-${k}`}
            className={`rounded-full px-4 py-2 text-xs font-bold border transition-colors ${tab === k ? "bg-[#16294A] text-white border-[#16294A]" : "border-slate-300 hover:border-[#16294A]"}`}>{l}</button>))}</div>
        <h2 className="mt-8 mb-5 text-2xl font-extrabold" data-testid="about-panel-title">{ABOUT_TABS.find(([k]) => k === tab)[1]}</h2>
        <div data-testid={`about-panel-${tab}`}><Panel /></div>
        <div className="mt-14 rounded-3xl bg-[#FCE4E7] p-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="font-extrabold text-xl">Every second counts. Get yours today.</div>
          <Link to="/sign-up" data-testid="about-cta" className="rounded-full bg-[#D7263D] text-white font-bold text-sm px-6 py-3 text-center">Get your bsafe ID</Link>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
